import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const HeaderContainer = styled(motion.div)`
  text-align: center;
  margin-bottom: 40px;
  padding: 40px 0;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, transparent, #22c55e, transparent);
    animation: scanLine 3s ease-in-out infinite;
  }
  
  @keyframes scanLine {
    0% { left: -100%; }
    50% { left: 100%; }
    100% { left: 100%; }
  }
`;

const LogoContainer = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  margin-bottom: 30px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
  }
`;

const Logo = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 20px;
  box-shadow: 0 8px 25px rgba(34, 197, 94, 0.3);
  transition: all 0.4s ease;
  animation: logoGlow 4s ease-in-out infinite;
  position: relative;
  z-index: 2;
  will-change: box-shadow, filter;
  transform: translateZ(0);

  &:hover {
    transform: scale(1.1) rotate(5deg) translateZ(0);
    box-shadow: 0 15px 40px rgba(34, 197, 94, 0.6);
  }

  @keyframes logoGlow {
    0%, 100% { 
      box-shadow: 0 8px 25px rgba(34, 197, 94, 0.3);
      filter: brightness(1);
    }
    50% { 
      box-shadow: 0 12px 35px rgba(34, 197, 94, 0.5);
      filter: brightness(1.1);
    }
  }

  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
  }
`;

const Title = styled(motion.h1)`
  font-size: 3.5rem;
  font-weight: 800;
  margin: 0;
  color: #22c55e;
  text-shadow: 0 0 20px rgba(34, 197, 94, 0.3);
  animation: glow 2s ease-in-out infinite alternate;

  @keyframes glow {
    from { text-shadow: 0 0 20px rgba(34, 197, 94, 0.3); }
    to { text-shadow: 0 0 30px rgba(34, 197, 94, 0.6); }
  }

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const Subtitle = styled(motion.div)`
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 30px;
  color: #22c55e;
  line-height: 1.6;
`;

const FomoText = styled(motion.div)`
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(22, 163, 74, 0.1) 100%);
  backdrop-filter: blur(25px);
  border-radius: 20px;
  padding: 35px;
  margin: 0 auto;
  max-width: 900px;
  border: 2px solid rgba(34, 197, 94, 0.4);
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.4), 0 0 30px rgba(34, 197, 94, 0.1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle, rgba(34, 197, 94, 0.1) 0%, transparent 70%);
    animation: pulse 6s ease-in-out infinite;
  }
  
  @keyframes pulse {
    0%, 100% { transform: scale(1) rotate(0deg); opacity: 0.3; }
    50% { transform: scale(1.1) rotate(180deg); opacity: 0.6; }
  }
  
  @media (max-width: 768px) {
    padding: 25px;
    border-radius: 15px;
    margin: 0 10px;
  }
`;

const FomoTitle = styled.h3`
  color: #22c55e;
  font-size: 1.8rem;
  margin-bottom: 20px;
  font-weight: 800;
  text-align: center;
  text-shadow: 0 2px 10px rgba(34, 197, 94, 0.4);
  position: relative;
  z-index: 2;
  animation: titleGlow 3s ease-in-out infinite alternate;
  
  @keyframes titleGlow {
    from { text-shadow: 0 2px 10px rgba(34, 197, 94, 0.4); }
    to { text-shadow: 0 4px 20px rgba(34, 197, 94, 0.8); }
  }
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 15px;
  }
`;

const FomoContent = styled.div`
  font-size: 1.2rem;
  line-height: 1.9;
  color: rgba(255, 255, 255, 0.95);
  text-align: center;
  position: relative;
  z-index: 2;
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    line-height: 1.8;
  }
`;

const Highlight = styled.span`
  color: #22c55e;
  font-weight: 800;
  text-shadow: 0 2px 8px rgba(34, 197, 94, 0.5);
  position: relative;
  display: inline-block;
  animation: highlightPulse 2s ease-in-out infinite;
  
  @keyframes highlightPulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(90deg, #22c55e, #16a34a, #22c55e);
    border-radius: 1px;
    animation: underlineGlow 2s ease-in-out infinite;
  }
  
  @keyframes underlineGlow {
    0%, 100% { opacity: 0.6; transform: scaleX(1); }
    50% { opacity: 1; transform: scaleX(1.1); }
  }
`;

function Header() {
  return (
    <HeaderContainer
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 1, delay: 0.2 }}
    >
      <LogoContainer
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.8, delay: 0.3 }}
      >
        <Logo src="/logo.png" alt="XWF Token Logo" />
        <Title
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          XWF Token Generator
        </Title>
      </LogoContainer>
      
      <Subtitle
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.6 }}
      >
        🎩 创造你的专属XWF头像 🎩
      </Subtitle>

      <FomoText
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.8 }}
      >
        {/* <FomoTitle>🔥 为什么选择XWF？</FomoTitle> */}
        <FomoContent>
          {/* <Highlight>XWF百倍币就在这里！</Highlight> 创造你的专属XWF头像，展示你的独特风格。 */}
          {/* 加入XWF社区，与志同道合的投资者一起分享经验和机会。 */}
          {/* 不要错过这个千载难逢的机会， */}
          <Highlight>现在就开始创造你的XWF头像吧！</Highlight>
        </FomoContent>
      </FomoText>
    </HeaderContainer>
  );
}

export default Header;
